/**
 * ProgressBar molecule subcomponents. Internal use only.
 *
 * @internal Used by ProgressBar molecule only.
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { Tag, Typography } from '@/components/ui/atoms';
import type { ProgressBarIntent, ProgressBarPalette } from './ProgressBar.types';
import { getFloatingTagFillClass } from '../../atoms/ProgressBar/ProgressBar.styles';

type TypographyVariant = 'body-xs' | 'body-sm' | 'body-md';

interface LabelTextProps {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
}

export const LabelText: React.FC<LabelTextProps> = ({ variant, children, className = '' }) => (
  <Typography as="span" variant={variant} color="secondary" className={className}>
    {children}
  </Typography>
);

interface ValueTextProps {
  variant: TypographyVariant;
  children: React.ReactNode;
  className?: string;
}

export const ValueText: React.FC<ValueTextProps> = ({ variant, children, className = '' }) => (
  <Typography as="span" variant={variant} weight="semibold" color="primary" className={className}>
    {children}
  </Typography>
);

interface SideLayoutProps {
  label?: React.ReactNode;
  value?: React.ReactNode;
  bar: React.ReactNode;
  className?: string;
}

export const SideLayout: React.FC<SideLayoutProps> = ({ label, value, bar, className = '' }) => (
  <div className={cn('w-full flex items-center gap-3', className)}>
    {label}
    <div className="flex-1 min-w-0">{bar}</div>
    {value}
  </div>
);

interface TopLabelRowProps {
  label?: React.ReactNode;
  value?: React.ReactNode;
}

export const TopLabelRow: React.FC<TopLabelRowProps> = ({ label, value }) => (
  <div className="flex justify-between items-center min-w-0 shrink-0">
    {label}
    {value}
  </div>
);

interface FloatingTagProps {
  percent: number;
  intent: ProgressBarIntent;
  palette: ProgressBarPalette;
  positionStyle: React.CSSProperties;
}

/** Floating percent Tag above the fill head; caret uses the same fill as the Tag intent. */
export const FloatingTag: React.FC<FloatingTagProps> = ({
  percent,
  intent,
  palette,
  positionStyle,
}) => {
  const caretClass = getFloatingTagFillClass(percent, intent, palette);
  return (
    <span
      className="absolute flex flex-col items-center -translate-x-1/2 pointer-events-none"
      style={positionStyle}
    >
      <Tag intent={intent} palette={palette} variant="solid">
        {percent}%
      </Tag>
      <div
        className={cn('w-2 h-1', caretClass)}
        style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)' }}
        aria-hidden
      />
    </span>
  );
};
